import React, { useState, useEffect } from 'react';
import { client, urlFor } from '../../client'; // Corrected path
import { PortableText } from '@portabletext/react';
// import LoadingSpinner from '../../components/LoadingSpinner'; // Removed import
// import ErrorMessage from '../../components/ErrorMessage'; // Removed import

// Placeholder for loading state (Copied from SecondGrade.js)
const LoadingSpinner = () => (
  <div className="flex justify-center items-center min-h-screen">
    {/* Using primary color from Tailwind config if available, otherwise default blue */}
    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-[#2a4865]"></div> 
    <p className="ml-4 text-lg text-gray-700">Loading...</p>
  </div>
);

// Placeholder for error state (Copied from SecondGrade.js, adjusted slightly for context)
const ErrorMessage = ({ message }) => (
  <div className="container mx-auto px-4 py-12 text-center">
    <p className="text-red-600 font-semibold">{message || 'Error loading page content. Please try again later.'}</p>
  </div>
);

const ThirdGrade = () => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(''); // Initialize activeTab state

  useEffect(() => {
    const query = `*[_type == "thirdGradePage" && _id == "thirdGradePageContent"][0]{
      pageTitle,
      bannerTitle,
      bannerImage {
        asset->{
          _id,
          url
        }
      },
      tabs[]{
        _key,
        tabTitle,
        tabIdentifier{
          current
        },
        contentSections[]{
          _key,
          title,
          body
        }
      },
      newsletterTitle,
      newsletterSubtitle,
      teacherName,
      newsletterContent
    }`;

    client.fetch(query)
      .then((data) => {
        if (data && data.tabs && data.tabs.length > 0) {
          setPageData(data);
          setActiveTab(data.tabs[0].tabIdentifier.current); // Set initial active tab
        } else {
          throw new Error('No third grade page data found');
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error('Failed to fetch third grade page data:', err);
        setError('Failed to load page content. Please try again later.');
        setLoading(false);
      });
  }, []);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;
  if (!pageData) return <ErrorMessage message="No content available for this page." />;

  const currentTabData = pageData.tabs.find(tab => tab.tabIdentifier.current === activeTab);

  return (
    <div className="bg-white">
      {/* Hero Banner with Image Overlay */}
      <section className="relative h-[40vh] max-h-[400px]">
        <div className="w-full h-full relative overflow-hidden">
          {pageData.bannerImage?.asset?.url && (
            <img 
              src={urlFor(pageData.bannerImage).url()} 
              alt={pageData.bannerTitle || 'PS52 Third Grade Banner'} 
              className="absolute inset-0 w-full h-full object-cover object-center" 
              loading="lazy"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-black/20"></div>
        </div>
        
        <div className="absolute inset-0 flex items-end">
          <div className="w-full px-6 md:px-12 lg:px-16">
            <div className="max-w-3xl pb-12 md:pb-16 lg:pb-16 text-left">
              <div className="w-16 h-1 bg-yellow-500 mb-6"></div>
              
              <h1 className="text-white text-4xl md:text-5xl font-bold mb-2 leading-tight text-left">
                {pageData.bannerTitle || 'Third Grade'}
              </h1>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="bg-white p-8 rounded-lg shadow-md max-w-6xl mx-auto">
            
            {/* Tabs - Use rounded button style (like SecondGrade.js) */}
            {pageData.tabs.length > 0 && (
              <div className="flex overflow-x-auto pb-2 mb-8"> {/* Changed container classes */}
                {pageData.tabs.map((tab) => (
                  <button
                    key={tab._key}
                    className={`py-3 px-6 font-bold rounded-full mr-4 transition-colors whitespace-nowrap ${ /* Changed button classes */
                      activeTab === tab.tabIdentifier.current
                        ? 'bg-[#691e31] text-white shadow-md' // Active style from SecondGrade
                        : 'bg-[#2a4865]/10 text-[#2a4865] hover:bg-[#2a4865]/20' // Inactive style from SecondGrade
                    }`}
                    onClick={() => setActiveTab(tab.tabIdentifier.current)}
                  >
                    {tab.tabTitle}
                  </button>
                ))}
                
                {/* Newsletter Tab */}
                <button
                  className={`py-3 px-6 font-bold rounded-full mr-4 transition-colors whitespace-nowrap ${
                    activeTab === 'newsletter'
                      ? 'bg-[#691e31] text-white shadow-md'
                      : 'bg-[#2a4865]/10 text-[#2a4865] hover:bg-[#2a4865]/20'
                  }`}
                  onClick={() => setActiveTab('newsletter')}
                >
                  {pageData.newsletterTitle || 'Monthly Newsletter'}
                </button>
              </div>
            )}

            {/* Tab Content */}
            {currentTabData && activeTab !== 'newsletter' ? (
              <div className="space-y-6">
                {currentTabData.contentSections.map((section) => (
                  <section key={section._key} className="bg-[#2a4865]/5 p-5 rounded-lg border-l-4 border-[#2a4865]">
                    <h3 className="text-xl font-bold text-[#2a4865] mb-3 relative after:content-[''] after:absolute after:w-10 after:h-0.5 after:bg-yellow-500 after:-bottom-1 after:left-0">
                      {section.title}
                    </h3>
                    {/* Render Portable Text Content */}
                    <div className="prose prose-sm sm:prose max-w-none text-gray-700">
                      <PortableText value={section.body} />
                    </div>
                  </section>
                ))}
              </div>
            ) : activeTab === 'newsletter' ? (
              <div className="space-y-6">
                <section className="bg-white border border-gray-200 rounded-lg shadow-sm">
                  <div className="bg-[#2a4865] text-white p-6 rounded-t-lg">
                    <h3 className="text-2xl font-bold mb-2 text-white">
                      {pageData.newsletterSubtitle || 'January 2025 Newsletter'}
                    </h3>
                    <p className="text-white/80 text-sm">
                      Third Grade Class • {pageData.teacherName || 'Ms. Garcia'}
                    </p>
                  </div>
                  
                  <div className="p-6 prose prose-lg max-w-none">
                    {pageData.newsletterContent && pageData.newsletterContent.length > 0 ? (
                      <PortableText value={pageData.newsletterContent} />
                    ) : (
                      <p className="text-gray-700">Newsletter content coming soon...</p>
                    )}
                  </div>
                </section>
              </div>
            ) : (
              <ErrorMessage message="Selected tab content not found." />
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ThirdGrade; 